import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { apiGet } from '@/api/client';

/* ---------------- Carriers ---------------- */

export interface Carrier {
  carrierId: number;
  name: string;
  tableName: string;
  carrierType: 'ACA' | 'Medicare' | null;
  brandColor: string | null;
  portalUrl: string | null;
  active: number;
  filesTotal: number;
}

export interface CarrierFile {
  id: number;
  carrierId: number;
  fileName: string;
  kind: 'contrato' | 'guia';
  sizeBytes: number | null;
  uploadedAt: string;
}

export function useCarriers() {
  return useQuery<{ carriers: Carrier[]; files: CarrierFile[] }>({
    queryKey: ['contracting', 'carriers'],
    queryFn: () => apiGet('/contracting/carriers'),
  });
}

export interface CarrierBlock {
  id: number;
  agentId: string;
  agentName: string;
  carrierId: number;
  carrierName: string;
  reason: string;
  blockedByName: string | null;
  createdAt: string;
}

export function useCarrierBlocks() {
  return useQuery<{ blocks: CarrierBlock[] }>({
    queryKey: ['contracting', 'carrier-blocks'],
    queryFn: () => apiGet('/contracting/carrier-blocks'),
  });
}

export interface TrackingRow {
  agentId: string;
  agentName: string;
  carrierName: string;
  brandColor: string | null;
  step: number;
  requestedAt: string | null;
  filesTotal: number;
}

export function useContractTracking() {
  return useQuery<{ requests: TrackingRow[] }>({
    queryKey: ['contracting', 'tracking'],
    queryFn: () => apiGet('/contracting/contract-requests'),
  });
}

/* ---------------- Notifications ---------------- */

export interface Notification {
  id: number;
  flow: 'onboarding' | 'contratos' | 'release' | 'otros';
  eventCode: string;
  title: string;
  body: string | null;
  icon: string | null;
  channel: 'email' | 'inapp' | 'both';
  actorName: string | null;
  actionLabel: string | null;
  actionUrl: string | null;
  createdAt: string;
  readAt: string | null;
}

export function useNotifications(flow?: string) {
  return useQuery<{ notifications: Notification[]; unread: number }>({
    queryKey: ['contracting', 'notifications', flow ?? 'all'],
    queryFn: () =>
      apiGet('/contracting/notifications', flow ? { flow } : undefined),
    placeholderData: keepPreviousData,
    refetchInterval: 60_000,
  });
}

export function useNotificationMatrix() {
  return useQuery<{
    matrix: { flow: string; event: string; to: string; channel: string }[];
  }>({
    queryKey: ['contracting', 'notification-matrix'],
    queryFn: () => apiGet('/contracting/notification-matrix'),
    staleTime: Infinity,
  });
}

export function useNotificationSubscriptions() {
  return useQuery<{ subscriptions: { flow: string; enabled: boolean }[] }>({
    queryKey: ['contracting', 'subscriptions'],
    queryFn: () => apiGet('/contracting/notification-subscriptions'),
  });
}

/* ---------------- Campaigns ---------------- */

export interface Campaign {
  id: number;
  name: string;
  subject: string;
  body: string;
  audience: 'todos' | 'activos' | 'medicare' | 'aca';
  channel: 'email' | 'inapp' | 'both';
  frequency: 'once' | 'weekly' | 'monthly' | 'quarterly';
  scheduleAt: string | null;
  nextRunAt: string | null;
  status: 'draft' | 'scheduled' | 'active' | 'sent' | 'cancelled';
  createdAt: string;
  sentTotal: number;
}

export function useCampaigns() {
  return useQuery<{ campaigns: Campaign[] }>({
    queryKey: ['contracting', 'campaigns'],
    queryFn: () => apiGet('/contracting/campaigns'),
  });
}

export function useAudienceCount(audience: string) {
  return useQuery<{ total: number }>({
    queryKey: ['contracting', 'audience-count', audience],
    queryFn: () =>
      apiGet('/contracting/campaigns/audience-count', { audience }),
    placeholderData: keepPreviousData,
  });
}

/* ---------------- Birthdays ---------------- */

export interface Birthday {
  agentId: string;
  name: string;
  email: string;
  dob: string;
  agencyName: string | null;
  isStaff: number;
  daysAway: number;
}

export function useUpcomingBirthdays() {
  return useQuery<{ birthdays: Birthday[] }>({
    queryKey: ['contracting', 'birthdays'],
    queryFn: () => apiGet('/contracting/birthdays/upcoming'),
  });
}

export function useBirthdaySettings() {
  return useQuery<{
    enabled: boolean;
    includeStaff: boolean;
    channel: 'email' | 'inapp' | 'both';
    template: string | null;
  }>({
    queryKey: ['contracting', 'birthday-settings'],
    queryFn: () => apiGet('/contracting/birthdays/settings'),
  });
}

export function useBirthdayLog() {
  return useQuery<{
    log: { name: string; sentAt: string; channel: string }[];
  }>({
    queryKey: ['contracting', 'birthday-log'],
    queryFn: () => apiGet('/contracting/birthdays/log'),
  });
}

/* ---------------- Reports ---------------- */

export interface ReportSummary {
  months: {
    month: string;
    contractsRequested: number;
    newAgents: number;
    releases: number;
  }[];
  totals: { contractsRequested: number; newAgents: number; releases: number };
  kpis: {
    contractsRequested: { value: number; deltaPct: number | null };
    newAgents: { value: number; deltaPct: number | null };
    releases: { value: number; delta: number };
    lastRequest: {
      date: string | null;
      carrierName: string | null;
      agentName: string | null;
    };
  };
  byCarrier: { carrierName: string; brandColor: string | null; total: number }[];
}

export function useReportSummary(filters: {
  agency?: number;
  carrier?: number;
}) {
  const params: Record<string, string> = {};
  if (filters.agency) params.agency = String(filters.agency);
  if (filters.carrier) params.carrier = String(filters.carrier);

  return useQuery<ReportSummary>({
    queryKey: ['contracting', 'report-summary', params],
    queryFn: () => apiGet('/contracting/reports/summary', params),
    placeholderData: keepPreviousData,
  });
}
